<template>
  <div v-if="pagamento.id" class="card card-body shadow-sm mt-3">
    <!-- Cabeçalho -->
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">Detalhes do Pagamento nº {{ pagamento.id }}</h4>
      <button class="btn btn-secondary" @click="voltar">Voltar</button>
    </div>

    <!-- Dados da Despesa -->
    <FormSection title="Dados da Despesa">
      <div class="info-group">
        <strong>Tipo:</strong> {{ pagamento.tipo }}
      </div>
      <div class="info-group">
        <strong>Descrição:</strong> {{ pagamento.descricao }}
      </div>
      <div class="info-group">
        <strong>Vencimento:</strong> {{ globalUtils.formatarData(pagamento.data_vencimento) }}
      </div>
      <div class="info-group">
        <strong>Valor principal:</strong> {{ globalUtils.formatarValor(pagamento.valor) }}
      </div>
      <div v-if="mostrarJurosEMulta" class="info-group">
        <strong>Juros:</strong> {{ globalUtils.formatarValor(pagamento.juros) }}
      </div>
      <div v-if="mostrarJurosEMulta" class="info-group">
        <strong>Multa:</strong> {{ globalUtils.formatarValor(pagamento.multa) }}
      </div>
      <div class="info-group">
        <strong>Desconto:</strong> {{ globalUtils.formatarValor(pagamento.desconto) }}
      </div>
      <div class="info-group">
        <strong>Total pago:</strong> {{ globalUtils.formatarValor(pagamento.valor_total_pago) }}
      </div>
    </FormSection>

    <!-- Detalhes do Pagamento -->
    <FormSection title="Detalhes do Pagamento">
      <div class="table-responsive">
        <table class="table table-bordered mb-0">
          <thead class="table-light">
            <tr>
              <th>Forma de Pagamento</th>
              <th>Data Pagamento</th>
              <th>Conta Financeira</th>
              <th v-if="showChequeCols">Banco</th>
              <th v-if="showChequeCols">Agência</th>
              <th v-if="showChequeCols">Cheque nº</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ pagamento.forma_pagamento }}</td>
              <td>
                {{ pagamento.data_pagamento
                  ? globalUtils.formatarData(pagamento.data_pagamento)
                  : '—' }}
              </td>
              <td>{{ contaTexto || '—' }}</td>
              <td v-if="showChequeCols">{{ pagamento.banco_numero || '—' }}</td>
              <td v-if="showChequeCols">{{ pagamento.agencia || '—' }}</td>
              <td v-if="showChequeCols">{{ pagamento.cheque_numero || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </FormSection>

    <!-- <div class="mt-3">
      <em>Registrado por: {{ pagamento.por || 'Desconhecido' }}</em>
    </div> -->
  </div>

  <div v-else>
    <LoadingSpinner />
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from '@/services/axios';
import FormSection from '@/components/Layout/FormSection.vue';
import globalUtils from '@/utils/globalUtils';
import LoadingSpinner from '@/utils/LoadingSpinner.vue';

const origem = computed(() => (route.query?.origem || '').toString());

const router = useRouter();
const route = useRoute();

// estado do pagamento
const pagamento = reactive({
  id: null,
  despesa_id: null,
  tipo: '',
  descricao: '',
  data_vencimento: '',
  valor: 0,
  juros: 0,
  multa: 0,
  desconto: 0,
  valor_total_pago: 0,
  forma_pagamento: '',
  data_pagamento: '',
  data_recebimento: '',
  conta_financeira_id: null,
  conta_financeira: '',
  banco_numero: '',
  agencia: '',
  cheque_numero: '',
  por: ''
});

const contasFinanceiras = ref([]);


// carregar o detalhe do pagamento
async function carregarPagamento(id) {
  // tenta com a origem informada; se não vier, tenta despesas -> entradas
  const tryFetch = (org) => axios.get(`/financeiro/despesas-completas/${id}`, {
    params: org ? { origem: org } : undefined
  });

  let res;
  try {
    if (origem.value) {
      res = await tryFetch(origem.value);
    } else {
      // fallback seguro quando a query não veio:
      try {
        res = await tryFetch('despesas');
        if (!res?.data?.data) throw new Error('not found');
      } catch {
        res = await tryFetch('entradas');
      }
    }
  } catch (err) {
    console.error('Erro ao carregar pagamento:', err);
    return;
  }

  const d = res.data.data;
  pagamento.id                 = d.id;
  pagamento.tipo               = d.tipo;
  pagamento.descricao          = d.descricao;
  pagamento.data_vencimento    = d.data_vencimento;
  pagamento.valor              = Number(d.valor || 0);
  pagamento.juros              = Number(d.juros || 0);
  pagamento.multa              = Number(d.multa || 0);
  pagamento.desconto           = Number(d.desconto || 0);
  pagamento.valor_total_pago   = Number(
    d.valor_total_pago ??
    (Number(d.valor || 0) + Number(d.juros || 0) + Number(d.multa || 0) - Number(d.desconto || 0))
  );
  pagamento.forma_pagamento    = d.forma_pagamento;
  pagamento.data_pagamento     = d.data_pagamento;
  pagamento.data_recebimento   = d.data_recebimento;
  pagamento.conta_financeira_id= d.conta_financeira_id;
  pagamento.conta_financeira   = d.conta_financeira;
  pagamento.banco_numero       = d.banco_numero;
  pagamento.agencia            = d.agencia;
  pagamento.cheque_numero      = d.cheque_numero;
  pagamento.por                = d.por;
}

const contaTexto = computed(() => {
  const acc = contasFinanceiras.value.find(c => c.id === pagamento.conta_financeira_id);
  return acc ? acc.texto : (pagamento.conta_financeira || '—');
});

// mostra colunas de cheque se houver cheque
const showChequeCols = computed(() =>
  (pagamento.forma_pagamento || '').toLowerCase() === 'cheque'
);

const mostrarJurosEMulta = computed(() => {
  const venc = String(pagamento.data_vencimento || '').trim();
  const pag = String(pagamento.data_pagamento || '').trim();
  if (!venc || !pag) return false;
  return pag > venc;
});

onMounted(async () => {
  // carrega contas financeiras para lookup
  const resC = await axios.get('/financeiro/contas-financeiras');
  contasFinanceiras.value = (resC.data.data || []).map(c => ({
    id: c.id,
    texto: `${c.apelido} | Ag ${c.agencia} | CC ${c.numero_conta}`
  }));

  if (route.params.id) {
    await carregarPagamento(route.params.id);
  }
});

function voltar() {
  router.back();
}
</script>

<style scoped>
.info-group {
  margin-bottom: 0.5rem;
}
.form-header {
  margin-bottom: 1rem;
}
</style>
